// src/Pages/AdminUsers.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import PageShell from "../Layout/PageShell";
import Topbar from "../Layout/Topbar";
import "../Home/Home.css";   // reuse the same header/toolbar/card styles
import "./lists.css";
import { addAdminUser, deleteAdminUser, editAdminUser, getAdminUsers, getRoles } from "../Api/Api";

const unwrapList = (res) => {
  const response = res?.data?.response;
  if (Array.isArray(response?.detail)) return response.detail;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;
  if (Array.isArray(res?.data?.detail)) return res.data.detail;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
};

const mapRole = (role) => ({
  id: role.role_id ?? role.id,
  name: role.role_name ?? role.name ?? "",
});

const mapAdmin = (admin) => ({
  id: admin.admin_id ?? admin.id ?? admin.user_id,
  name: admin.name ?? admin.full_name ?? admin.admin_name ?? "",
  email: admin.email ?? "",
  status: admin.status ?? (admin.is_active === 0 ? "Inactive" : "Active"),
  role_id: admin.role_id ?? admin.role?.id ?? "",
});

/* -------------------- Component -------------------- */
export default function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [q, setQ] = useState("");
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState("");

  // Add/Edit modal state
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const emptyForm = { name: "", email: "", password: "", role_id: "" };
  const [form, setForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState({ id: null, name: "", email: "", role_id: "", password: "" });

  const refresh = async () => {
    setReloading(true);
    setError("");
    try {
      const [adminRes, roleRes] = await Promise.all([getAdminUsers(), getRoles()]);
      setRows(unwrapList(adminRes).map(mapAdmin).filter((admin) => admin.id));
      setRoles(unwrapList(roleRes).map(mapRole).filter((role) => role.id));
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load admin users.");
    } finally {
      setReloading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const roleNameById = useCallback(
    (id) => roles.find(r => r.id === Number(id))?.name || "-",
    [roles]
  );

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s
      ? rows.filter(
          (r) =>
            r.name.toLowerCase().includes(s) ||
            r.email.toLowerCase().includes(s) ||
            r.status.toLowerCase().includes(s) ||
            roleNameById(r.role_id).toLowerCase().includes(s)
        )
      : rows;
  }, [rows, q, roleNameById]);

  /* -------------------- Create -------------------- */
  const onSave = async () => {
    if (!form.name || !form.email || !form.password || !form.role_id) {
      alert("All fields (including role) are required");
      return;
    }
    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      password: form.password, // send to backend
      status: "Active",
      role_id: Number(form.role_id),
    };
    try {
      await addAdminUser(payload);
      setForm(emptyForm);
      setShowAdd(false);
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to create admin user.");
    }
  };

  /* -------------------- Edit -------------------- */
  const openEdit = (row) => {
    setEditForm({
      id: row.id,
      name: row.name,
      email: row.email,
      role_id: row.role_id || "",
      password: "", // optional; empty means "do not change"
    });
    setShowEdit(true);
  };

  const onUpdate = async () => {
    if (!editForm.name || !editForm.email || !editForm.role_id) {
      alert("Name, Email and Role are required");
      return;
    }
    const payload = {
      name: editForm.name.trim(),
      email: editForm.email.trim(),
      role_id: Number(editForm.role_id),
      // Only send password if user typed one
      ...(editForm.password ? { password: editForm.password } : {}),
      status: rows.find(r => r.id === editForm.id)?.status || "Active",
    };
    try {
      await editAdminUser({ id: editForm.id, ...payload });
      setShowEdit(false);
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update admin user.");
    }
  };

  /* -------------------- Delete -------------------- */
  const onDelete = async (id) => {
    if (!window.confirm("Delete this admin user?")) return;
    try {
      await deleteAdminUser(id);
      setRows(p => p.filter(r => r.id !== id));
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to delete admin user.");
    }
  };

  return (
    <PageShell>
      {/* SAME HEADER AS HOME */}
      <Topbar onRefresh={refresh} refreshing={reloading} />
      {error && <div className="inline-error">{error}</div>}

      {/* SAME TOOLBAR LAYOUT + FORCED SPACING TO MATCH HOME */}
      <div
        className="toolbar"
        style={{ margin: "14px 0", padding: "0 20px", gap: 10 }}
      >
        <div className="search">
          <input
            type="text"
            placeholder="Search company, name, or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="right-controls" style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            className="btn primary"
            onClick={() => {
              setForm(emptyForm);       // ensure NO PREFILL
              setShowAdd(true);
            }}
          >
            + Add New User
          </button>
        </div>
      </div>

      {/* CONTENT CARD */}
      <div className="card">
        <div className="card-head">
          <h3>Admin Users</h3>
          <div className="chips">
            <span className="chip">Total: {rows.length}</span>
            <span className="chip ok">Shown: {filtered.length}</span>
          </div>
        </div>

        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th>Role</th>
                <th style={{ width: 120, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td>{r.email}</td>
                  <td>
                    <span className={`badge ${r.status === "Active" ? "ok" : "warn"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td>{roleNameById(r.role_id)}</td>
                  <td style={{ textAlign: "right" }}>
                    <button className="icon" title="Edit" onClick={() => openEdit(r)}>✏️</button>
                    <button className="icon" title="Delete" onClick={() => onDelete(r.id)}>🗑️</button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="empty">No admins</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Admin Modal */}
      {showAdd && (
        <div className="modal">
          <div className="dialog pro">
            <div className="dialog-head">
              <h4>Add Admin User</h4>
              <p className="muted">All fields are required.</p>
            </div>

            <div className="form-grid">
              <div className="field">
                <label>Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  autoComplete="off"
                />
              </div>
              <div className="field">
                <label>Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  autoComplete="off"
                />
              </div>
              <div className="field">
                <label>Password</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  autoComplete="new-password"
                />
              </div>
             <div className="field">
                <label>Role</label>
                <div className="select-wrap">
                    <select
                    value={form.role_id}
                    onChange={(e) => setForm({ ...form, role_id: e.target.value })}
                    >
                    <option value="">Select role…</option>
                    {roles.map(r => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                    </select>
                </div>
                </div>

            </div>

            <div className="dialog-actions">
              <button className="btn primary" onClick={onSave}>Save</button>
              <button className="btn light" onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Admin Modal */}
      {showEdit && (
        <div className="modal">
          <div className="dialog pro">
            <div className="dialog-head">
              <h4>Edit Admin User</h4>
              <p className="muted">Update user information. Leave password empty to keep unchanged.</p>
            </div>

            <div className="form-grid">
              <div className="field">
                <label>Name</label>
                <input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  autoComplete="off"
                />
              </div>
              <div className="field">
                <label>Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  autoComplete="off"
                />
              </div>
              <div className="field">
                <label>New Password <span className="dim">(optional)</span></label>
                <input
                  type="password"
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  autoComplete="new-password"
                  placeholder="••••••••"
                />
              </div>
             <div className="field">
                <label>Role</label>
                <div className="select-wrap">
                    <select
                    value={editForm.role_id}
                    onChange={(e) => setEditForm({ ...editForm, role_id: e.target.value })}
                    >
                    <option value="">Select role…</option>
                    {roles.map(r => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                    </select>
                </div>
                </div>

            </div>

            <div className="dialog-actions">
              <button className="btn primary" onClick={onUpdate}>Save</button>
              <button className="btn light" onClick={() => setShowEdit(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
